// Big Bytes slot machine sounds: the coin going in, each reel stopping and
// the jingle on a win. They plug into the Sound system in audio.ts (like the
// spell sounds), so the volume settings apply and a recorded file at
// /sounds/<name>.mp3 (e.g. sounds/coin.mp3) replaces the synth. The looping
// spin song is in slotSong.ts.
//
// This module must not import audio.ts at runtime (audio.ts imports it).

export const SLOT_SOUNDS = ["coin", "reelStop", "slotWin"] as const;
export type SlotSound = (typeof SLOT_SOUNDS)[number];

/** Base loudness of each slot sound before the volume settings (0..1). */
export const SLOT_SOUND_LEVEL: Record<SlotSound, number> = {
  coin: 0.35,
  reelStop: 0.28,
  slotWin: 0.3,
};

type Synth = (ctx: AudioContext, out: AudioNode, t0: number) => void;

function ping(ctx: AudioContext, out: AudioNode, t0: number, freq: number, ms: number, type: OscillatorType, level: number): void {
  const t1 = t0 + ms / 1000;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(level, t0 + 0.004);
  gain.gain.exponentialRampToValueAtTime(0.0001, t1);
  osc.connect(gain).connect(out);
  osc.start(t0);
  osc.stop(t1 + 0.02);
}

function tick(ctx: AudioContext, out: AudioNode, t0: number, ms: number, freq: number, level: number): void {
  const length = Math.ceil((ctx.sampleRate * ms) / 1000);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const f = ctx.createBiquadFilter();
  f.type = "bandpass";
  f.frequency.value = freq;
  f.Q.value = 2;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(level, t0 + 0.002);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + ms / 1000);
  src.connect(f).connect(gain).connect(out);
  src.start(t0);
  src.stop(t0 + ms / 1000 + 0.02);
}

/** A metal coin: a bright inharmonic clink (a few partials ringing together). */
function clink(ctx: AudioContext, out: AudioNode, t: number, level: number): void {
  for (const [f, l] of [
    [2637, 0.5],
    [3951, 0.35],
    [5274, 0.22],
    [6620, 0.12],
  ] as const) {
    ping(ctx, out, t, f * (0.99 + Math.random() * 0.02), 260, "sine", l * level);
  }
  tick(ctx, out, t, 12, 7000, 0.4 * level);
}

export const SLOT_SYNTHS: Record<SlotSound, Synth> = {
  // The coin hits the slot (clink), then rattles down the chute into the box.
  coin: (ctx, out, t0) => {
    clink(ctx, out, t0, 1);
    let t = t0 + 0.11;
    let gap = 0.05;
    for (let i = 0; i < 7; i++) {
      tick(ctx, out, t, 18, 2500 + Math.random() * 2500, 0.5 - i * 0.05);
      if (i % 3 === 0) ping(ctx, out, t, 1800 + Math.random() * 1200, 60, "triangle", 0.12);
      t += gap;
      gap *= 0.82;
    }
    clink(ctx, out, t + 0.03, 0.35);
  },

  // A reel locking in place: a mechanical clunk and a short bell "ding".
  reelStop: (ctx, out, t0) => {
    tick(ctx, out, t0, 30, 900, 0.7);
    ping(ctx, out, t0, 140, 70, "square", 0.25);
    ping(ctx, out, t0 + 0.02, 1568, 320, "sine", 0.55);
    ping(ctx, out, t0 + 0.02, 3136, 200, "sine", 0.15);
  },

  // A chiptune fanfare: a rising arpeggio, then a held major chord with bells.
  slotWin: (ctx, out, t0) => {
    const run = [523, 659, 784, 1047, 784, 1047, 1319];
    run.forEach((f, i) => ping(ctx, out, t0 + i * 0.075, f, 110, "square", 0.22));
    const tc = t0 + run.length * 0.075;
    for (const f of [1047, 1319, 1568]) ping(ctx, out, tc, f, 700, "square", 0.14);
    for (let i = 0; i < 5; i++) ping(ctx, out, tc + 0.1 + i * 0.09, 2093 + i * 330, 160, "sine", 0.18);
  },
};
